"use client";

import React from "react";
import { Leaf, ShieldCheck, ThermometerSun, Recycle, ArrowRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const carouselImages = [{
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1578090/img/biodegradable_cutlery_set.png",
  alt: "Premium biodegradable cutlery set in use"
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1578090/img/eco_friendly_paper_plates.png",
  alt: "Durable eco-friendly paper plates"
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1578090/img/plant_based_packaging.png",
  alt: "Plant-based sustainable packaging solutions"
}];
const qualityBadges = [{
  label: "100% Biodegradable",
  icon: Recycle
}, {
  label: "Compostable",
  icon: Leaf
}, {
  label: "Toxin-Free",
  icon: ShieldCheck
}, {
  label: "Heat Resistant",
  icon: ThermometerSun
}];
const technicalSpecs = [{
  feature: "Material Composition",
  spec: "Plant-based Biomass"
}, {
  feature: "Heat Resistance",
  spec: "Up to 100°C (212°F)"
}, {
  feature: "Decomposition Rate",
  spec: "60 - 90 Days"
}, {
  feature: "Microwave Safe",
  spec: "Yes (Up to 2 mins)"
}, {
  feature: "Freezer Safe",
  spec: "Yes"
}, {
  feature: "Chemical Additives",
  spec: "0% (PFAS-Free)"
}];
export default function SustainableSolutionsShowcase() {
  const {
    ref: leftRef,
    isVisible: isLeftVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: rightRef,
    isVisible: isRightVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="sustainable-solutions-showcase" className="bg-background py-16 md:py-24 overflow-hidden">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          
          {}
          <div ref={leftRef} className={`lg:col-span-7 transition-all duration-1000 ease-out ${isLeftVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"}`}>
            <div className="relative rounded-3xl bg-card p-2 shadow-xl border border-border/50">
              <div className="absolute inset-0 bg-gradient-to-tr from-primary/5 to-secondary/5 rounded-3xl pointer-events-none" />
              <Carousel opts={{
              loop: true,
              align: "center"
            }} className="w-full rounded-2xl overflow-hidden">
                <CarouselContent>
                  {carouselImages.map((image, i) => <CarouselItem key={i} data-index={i}>
                      <div className="relative aspect-[4/3] sm:aspect-video w-full overflow-hidden rounded-2xl">
                        <img src={image.src} alt={image.alt} className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 hover:scale-105" />
                        {}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                      </div>
                    </CarouselItem>)}
                </CarouselContent>
                <div className="hidden sm:block">
                  <CarouselPrevious className="left-4 bg-background/80 backdrop-blur-sm hover:bg-background" />
                  <CarouselNext className="right-4 bg-background/80 backdrop-blur-sm hover:bg-background" />
                </div>
              </Carousel>
            </div>
          </div>

          {}
          <div ref={rightRef} className={`lg:col-span-5 flex flex-col justify-center transition-all duration-1000 delay-200 ease-out ${isRightVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"}`}>
            <div className="inline-flex items-center gap-2 text-secondary font-medium mb-4">
              <Leaf className="size-5" />
              <span className="tracking-wide uppercase text-sm">Eco-Conscious Line</span>
            </div>
            
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4 leading-tight">
              Premium Plant-Based <br className="hidden lg:block" />
              <span className="text-primary">Biodegradable Products</span>
            </h2>
            
            <p className="text-muted-foreground mb-8 text-lg leading-relaxed">
              Our dedicated line of eco-friendly paper plates and cutlery offers a sustainable alternative without compromising on durability. Designed for both commercial and personal use.
            </p>

            {}
            <div className="flex flex-wrap gap-3 mb-10">
              {qualityBadges.map((badge, i) => {
              const Icon = badge.icon;
              return <Badge key={i} data-index={i} variant="secondary" className="px-3 py-1.5 text-sm bg-secondary/10 text-secondary-foreground hover:bg-secondary/20 border border-secondary/20 shadow-sm">
                    <Icon className="size-4 mr-1.5 text-secondary" />
                    {badge.label}
                  </Badge>;
            })}
            </div>

            {}
            <div className="rounded-xl border border-border bg-card shadow-sm overflow-hidden mb-8">
              <Table>
                <TableHeader className="bg-muted/30">
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="w-1/2 font-semibold text-foreground">Technical Specification</TableHead>
                    <TableHead className="w-1/2 font-semibold text-foreground">Rating / Value</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {technicalSpecs.map((item, i) => <TableRow key={i} data-index={i} className="hover:bg-muted/20">
                      <TableCell className="font-medium text-muted-foreground">{item.feature}</TableCell>
                      <TableCell className="text-foreground">{item.spec}</TableCell>
                    </TableRow>)}
                </TableBody>
              </Table>
            </div>

            {}
            <div>
              <Button asChild size="lg" className="w-full sm:w-auto group">
                <Link to="/ProductsPage">
                  Explore Full Catalog
                  <ArrowRight className="ml-2 size-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </Button>
            </div>
            
          </div>
        </div>
      </div>
    </section>;
}